import { forwardRef } from 'react';
import type { InputHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string | undefined;
  hint?: string;
  fullWidth?: boolean;
}

export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(
  ({ label, error, hint, id, className, fullWidth, ...props }, ref) => {
    const inputId = id ?? `f-${label.replace(/\s+/g, '-').toLowerCase()}`;
    return (
      <div className={cn('space-y-1.5', fullWidth && 'sm:col-span-2', className)}>
        <Label htmlFor={inputId}>
          {label}
          {props.required && <span className="text-destructive"> *</span>}
        </Label>
        <Input id={inputId} ref={ref} aria-invalid={!!error} {...props} />
        {error && <p className="text-xs text-destructive">{error}</p>}
        {!error && hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
    );
  },
);
FormField.displayName = 'FormField';

interface FormTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string | undefined;
  hint?: string;
  fullWidth?: boolean;
}

export const FormTextarea = forwardRef<HTMLTextAreaElement, FormTextareaProps>(
  ({ label, error, hint, id, className, fullWidth, ...props }, ref) => {
    const inputId = id ?? `f-${label.replace(/\s+/g, '-').toLowerCase()}`;
    return (
      <div className={cn('space-y-1.5', fullWidth && 'sm:col-span-2', className)}>
        <Label htmlFor={inputId}>
          {label}
          {props.required && <span className="text-destructive"> *</span>}
        </Label>
        <textarea
          id={inputId}
          ref={ref}
          aria-invalid={!!error}
          className={cn(
            'flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm',
            'placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
          {...props}
        />
        {error && <p className="text-xs text-destructive">{error}</p>}
        {!error && hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
    );
  },
);
FormTextarea.displayName = 'FormTextarea';

interface FormSelectProps extends Omit<
  InputHTMLAttributes<HTMLSelectElement>,
  'value' | 'onChange'
> {
  label: string;
  error?: string | undefined;
  hint?: string;
  fullWidth?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}

export const FormSelect = forwardRef<HTMLSelectElement, FormSelectProps>(
  (
    {
      label,
      error,
      hint,
      id,
      className,
      fullWidth,
      value,
      onChange,
      options,
      placeholder,
      ...props
    },
    ref,
  ) => {
    const inputId = id ?? `f-${label.replace(/\s+/g, '-').toLowerCase()}`;
    return (
      <div className={cn('space-y-1.5', fullWidth && 'sm:col-span-2', className)}>
        <Label htmlFor={inputId}>
          {label}
          {props.required && <span className="text-destructive"> *</span>}
        </Label>
        <select
          id={inputId}
          ref={ref}
          value={value ?? ''}
          onChange={(e) => onChange?.(e.target.value)}
          aria-invalid={!!error}
          className={cn(
            'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {error && <p className="text-xs text-destructive">{error}</p>}
        {!error && hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
    );
  },
);
FormSelect.displayName = 'FormSelect';
